/**
 * 未割当ピンの寸法と画像。V3Plans と同じものを使う
 * (V3Plans/presenters/PlanningMapOrderMarker.presenter.tsx:30-34)。
 *
 * 14px の丸を自前で描いていたが、クラスターが解けた後にどこにあるか分からないという
 * 指摘があった。旧地図と同じピンなら、画面をまたいでも同じ見え方になる。
 */
export const UNASSIGNED_PIN = {
  iconUrl: '/static/images/markers/red.png',
  iconSize: [24, 42],
  iconAnchor: [12, 42],
  tooltipAnchor: [-12, -42],
} as const;

/**
 * 未割当ピンをクラスターにまとめる距離 (px)。**旧地図 `/plans` と同じまとまり方にする値**。
 *
 * Why 数値で持つ: MarkerClusterGroup (src/components/MarkerClusterGroup.tsx:44-52) は
 * `on` で始まらない関数 prop を握り潰す。そのため新旧マップとも
 * `(zoom) => 100 / zoom` は L.MarkerClusterGroup へ渡っておらず、実際には
 * 既定の 80px が効いている（実ページの `options.maxClusterRadius` で確認済み）。
 * 暗黙の既定値に頼ると、共有コンポーネント側が直った日に新地図の見た目だけが黙って
 * 変わるので、効いている値をここに明示する。
 *
 * Why 80（＝旧地図と同じ）: 一度 8px まで絞ったが、旧地図 `/plans` と並べると
 * 新地図だけピンが散らばって「まとまり方が違う」となった（2026-08-31 ユーザー指摘）。
 * 見比べる相手は旧地図なので、そちらに合わせる。
 *
 * Why これで #2430 の報告例が再発しないか: 報告された症状は「**同一地点**に案件数ぶんの
 * ピンが立ち、クラスターの丸 (40px・アンカーは地点の中心) が同じ点の配車済み番号マーカーを
 * 覆う」というもの。同一座標のピンは半径をいくら絞っても必ず束ねられるので、
 * 効いているのは半径ではなく buildMapUnassignedMarkers の**地点集約**のほう。
 *
 * **残っている限界（半径の大小に関わらず消えない）**: 未割当の**別地点が 2 つ以上**
 * 近くにあると、その平均位置にクラスターの丸が立つ。最大ズーム (18) で 1px ≒ 0.49m
 * なので、丸の中心から約 9.8m 以内にある配車済み番号マーカーは中心を覆われて押せない。
 * 到達経路は 2 つある。
 * - 大型物流施設に別テナントの納品先が数 m 差で並ぶ（実データに複数あり。例:
 *   「サミット草加DC」と「日本アクセス草加物流センター」が 4.3m 差）
 * - 同一地点が座標のブレで 6 桁丸めでも別キーになる（#314 の残り。実データの
 *   waypoint 9,989 地点中 198 地点が複数キーへ割れ、うち 47 地点は 8.8m 以内）
 *
 * これは本ブランチが作り込んだものではなく、**修正前から同じ**（半径は前から実質 80px）。
 * spiderfy も逃げ道にならない: 展開中もクラスターアイコンは opacity 0.3 で DOM に残り、
 * pointer-events は auto のままなので同じ位置でクリックを奪い続ける（base で実測）。
 * 直すならクラスターアイコン側（アンカーを下端にする／配車済みマーカーを上のペインへ移す）
 * で、半径や丸め桁数をいじっても解決しない。
 */
export const UNASSIGNED_CLUSTER_RADIUS_PX = 80;

/**
 * 未選択マーカーの不透明度。
 *
 * 番号が読める濃さに留める。以前 0.15 まで落として「全体の配置が読み取れない」と
 * 廃止した経緯があるので、そこまでは落とさない。
 */
export const UNSELECTED_MARKER_OPACITY = 0.45;

/**
 * マーカーの不透明度。
 *
 * 選択中と未選択が同じ濃さだと、どれを選んでいるか分かりにくい（指摘 2-4）。
 * 未選択は薄くするが、番号が読める濃さ (UNSELECTED_MARKER_OPACITY) に留める。
 *
 * Why not 選択が無いときも薄くする: 選んでいないうちは差を示す必要が無く、
 * 画面を開いた直後に地図全体が薄いだけになる。
 */
export function markerOpacity({ hasSelection, isSelected }: { hasSelection: boolean; isSelected: boolean }): number {
  if (!hasSelection || isSelected) return 1;
  return UNSELECTED_MARKER_OPACITY;
}
